import type { CustomReactSelect, SelectList } from '../components/select-field';

import { useCallback, useEffect, useReducer } from 'react';

const DEFAULT_VALUES = {
  defaultValues: [],
  inputValue: '',
  isLoading: false,
  options: [],
};

export type UseSelectAsyncProps = {
  defaultOptions?: SelectList;
  loadOnRender?: boolean;
  loadOptions?: (
    search?: string,
    isInfiniteLoading?: boolean,
  ) => Promise<SelectList>;
};

export type UseSelectAsyncState = Pick<
  CustomReactSelect,
  'isLoading' | 'inputValue' | 'options'
> & { defaultValues: SelectList };

export type UseSelectAsyncAction =
  | {
      type: 'addDefaults';
      value: SelectList;
    }
  | {
      type: 'addOptions';
      value: SelectList;
    }
  | {
      type: 'initialLoad';
      value: SelectList;
    }
  | {
      type: 'newOptions';
      value: SelectList;
    }
  | {
      type: 'toggleLoading';
      value: UseSelectAsyncState['isLoading'];
    }
  | {
      type: 'updateInput';
      value: UseSelectAsyncState['inputValue'];
    }
  | { type: 'reset'; value?: Partial<UseSelectAsyncState> };

export type UseSelectAsyncReturn = UseSelectAsyncState & {
  filterOption: () => true;
  onInputChange: (value: string) => Promise<void>;
  onMenuScrollToBottom: () => Promise<void>;
};

const initState = initialState => ({
  ...DEFAULT_VALUES,
  ...initialState,
});

const reducerAction = (
  state: UseSelectAsyncState,
  action: UseSelectAsyncAction,
): UseSelectAsyncState => {
  switch (action.type) {
    case 'addDefaults': {
      const options = [...state.options, ...action.value];

      return {
        ...state,
        defaultValues: options,
        options,
      };
    }
    case 'addOptions': {
      return {
        ...state,
        options: [...state.options, ...action.value],
      };
    }
    case 'initialLoad': {
      const options = action.value;

      return {
        ...state,
        defaultValues: options,
        options,
      };
    }
    case 'newOptions': {
      return {
        ...state,
        options: action.value,
      };
    }
    case 'toggleLoading': {
      return {
        ...state,
        isLoading: action.value,
      };
    }
    case 'updateInput': {
      return {
        ...state,
        inputValue: action.value,
      };
    }
    case 'reset': {
      return initState({
        ...state,
        ...action?.value,
        options: state.defaultValues || action?.value?.defaultValues,
      });
    }
  }
};

export const useSelectAsync = ({
  defaultOptions: defaultValues = [],
  loadOnRender = false,
  loadOptions,
}: UseSelectAsyncProps): UseSelectAsyncReturn => {
  const [state, dispatch] = useReducer(
    reducerAction,
    { defaultValues, options: defaultValues },
    initState,
  );

  const filterOptions = useCallback(
    async (
      search?: string,
      isInfiniteLoad?: boolean,
      isInitialLoad?: boolean,
    ): Promise<void> => {
      if (!isInfiniteLoad && !isInitialLoad && !search) {
        dispatch({
          type: 'reset',
        });

        return;
      }

      try {
        dispatch({
          type: 'toggleLoading',
          value: true,
        });

        const newOptions = await loadOptions(search, isInfiniteLoad);
        let action: UseSelectAsyncAction['type'] = 'newOptions';

        if (isInitialLoad) {
          action = 'initialLoad';
        }

        if (isInfiniteLoad) {
          action = 'addOptions';

          if (!search) {
            action = 'addDefaults';
          }
        }

        dispatch({
          type: action,
          value: newOptions,
        });
      } catch (error) {
        dispatch({
          type: 'reset',
        });

        throw error;
      } finally {
        dispatch({
          type: 'toggleLoading',
          value: false,
        });
      }
    },
    [dispatch, loadOptions],
  );

  const onInputChange = useCallback(
    async (value: string): Promise<void> => {
      dispatch({
        type: 'updateInput',
        value,
      });

      return await filterOptions(value);
    },
    [filterOptions, dispatch],
  );

  const onMenuScrollToBottom = useCallback(async (): Promise<void> => {
    if (state.isLoading) {
      return;
    }

    return await filterOptions(state.inputValue, true);
  }, [state.inputValue, state.isLoading]);

  useEffect(() => {
    const getInitialOptions = async () =>
      loadOnRender
        ? await filterOptions(DEFAULT_VALUES.inputValue, false, true)
        : null;

    getInitialOptions();
  }, []);

  return {
    filterOption: useCallback(() => true, []),
    onInputChange,
    onMenuScrollToBottom,
    ...state,
  };
};
